import {
  type LucideIcon,
  BotMessageSquare,
  Boxes,
  Building,
  ChevronsLeft,
  ChevronsRight,
  House,
  KeyRound,
  Logs,
  Network,
  Settings,
  UserGroup,
  Users,
} from "lucide-react";
import { NavLink } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { oidcConfig } from "@/config";
import { hasRole } from "@/lib/hasRole";
import { Avatar } from "./Avatar";

const iconClass = "h-5 w-5 shrink-0";

export function Sidebar({
  expanded,
  onToggle,
  menuOpen,
  onNavigate,
}: {
  expanded: boolean;
  onToggle: () => void;
  menuOpen: boolean;
  onNavigate: () => void;
}) {
  const { user } = useAuth();
  const showLabels = expanded || menuOpen;

  const orgPath = user?.orgId ? `/${user.orgId}` : "/";

  const navItems: {
    label: string;
    to: string;
    icon: LucideIcon;
  }[] = [
    { label: "Dashboard", to: orgPath, icon: House },
    { label: "Users", to: `${orgPath}/users`, icon: Users },
    { label: "Teams", to: `${orgPath}/teams`, icon: UserGroup },
    { label: "Tokens", to: `${orgPath}/tokens`, icon: KeyRound },
    {
      label: "Networking",
      to: `${orgPath}/networking`,
      icon: Network,
    },
    { label: "Resources", to: `${orgPath}/resources`, icon: Boxes },
    ...(hasRole(user?.roles, "Admin")
      ? [{ label: "AI", to: `${orgPath}/ai`, icon: BotMessageSquare }]
      : []),
    ...(hasRole(user?.roles, "Admin")
      ? [{ label: "Logs", to: `${orgPath}/logs`, icon: Logs }]
      : []),
    ...(hasRole(user?.roles, "Admin")
      ? [{ label: "Settings", to: `${orgPath}/settings`, icon: Settings }]
      : []),
  ];

  return (
    <aside
      className={`absolute bottom-0 left-0 z-30 flex-col overflow-y-auto overflow-x-hidden bg-background text-foreground md:top-0 md:flex md:transition-[width] md:duration-200 ${
        menuOpen ? "top-14 flex w-full" : "hidden"
      } ${expanded ? "md:w-64" : "md:w-16"}`}
    >
      <div className="h-4 shrink-0" />
      <nav className="flex flex-1 flex-col gap-1 px-3">
        {navItems.map(({ label, to, icon: Icon }) => (
          <NavLink
            key={label}
            to={to}
            end={to === orgPath}
            title={showLabels ? undefined : label}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium max-md:py-3 max-md:text-[16px] ${
                showLabels ? "" : "justify-center"
              } ${isActive ? "bg-base text-foreground" : "text-foreground hover:bg-base hover:text-foreground"}`
            }
          >
            <Icon className={iconClass} />
            {showLabels && <span className="whitespace-nowrap">{label}</span>}
          </NavLink>
        ))}
      </nav>
      {user && (
        <a
          href={`${oidcConfig.authority}/accounts/organization?returnUrl=${encodeURIComponent(`${window.location.origin}/`)}`}
          className={`mx-3 mb-1 flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium max-md:py-3 max-md:text-[16px] text-foreground hover:bg-base ${
            showLabels ? "" : "justify-center"
          }`}
          title={
            showLabels
              ? undefined
              : `Switch organization${user.orgName ? ` — ${user.orgName}` : ""}`
          }
          aria-label={`Switch organization${user.orgName ? `, currently ${user.orgName}` : ""}`}
        >
          <Building className={iconClass} />
          {showLabels && (
            <span className="min-w-0">
              <span className="block whitespace-nowrap">
                Switch organization
              </span>
              {user.orgName && (
                <span className="block truncate text-xs font-normal text-foreground/70">
                  {user.orgName}
                </span>
              )}
            </span>
          )}
        </a>
      )}
      {user && (
        <NavLink
          to={`${orgPath}/profile`}
          onClick={onNavigate}
          title={
            showLabels ? undefined : (user.name ?? user.email ?? undefined)
          }
          className={({ isActive }) =>
            `mx-3 mb-1 flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium max-md:py-3 max-md:text-[16px] ${
              showLabels ? "" : "justify-center"
            } ${isActive ? "bg-base text-foreground" : "text-foreground hover:bg-base hover:text-foreground"}`
          }
        >
          <Avatar
            name={user.name}
            email={user.email}
            avatarId={user.avatarId}
          />
          {showLabels && (
            <span className="whitespace-nowrap">{user.name ?? user.email}</span>
          )}
        </NavLink>
      )}
      <button
        type="button"
        onClick={onToggle}
        title={expanded ? "Collapse sidebar" : "Expand sidebar"}
        aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
        className="mx-3 mb-3 hidden items-center justify-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-base hover:text-foreground md:flex"
      >
        {expanded ? (
          <ChevronsLeft className={iconClass} />
        ) : (
          <ChevronsRight className={iconClass} />
        )}
      </button>
    </aside>
  );
}
